<template lang="pug">
  management-page(title="Overview"
                  :creatable="false"
                  :searchable="false"
                  :showHeader="false")
    template(v-slot:body)
      .chart-configuration
        .time-range-filter
          aiq-date-picker(v-model="dateRange"
                          type="daterange"
                          placeholder="Select date range"
                          popper-class="metrics-time-range__date-picker-popup"
                          :shortcuts="datePickerOptions.shortcuts"
                          :disabled-date="datePickerOptions.disabledDate"
                          :teleported="false"
                          format="YYYY/MM/DD"
                          :clearable="false"
                          :default-time="getDatePickerDefaultTime()")
      .tiles-card
        .tiles-card-header
          aiq-button.export-tiles(@click="exportTiles" :disabled="loading")
            i.iq-ico-export
        .tiles-grid
          smart-assist-tile(v-for="tile in tiles"
                            :key="tile.key"
                            :title="tileTitle(tile)"
                            :category="tile.category"
                            :help-text="tileHelp(tile)"
                            :highlight="tile.highlight"
                            :value="tileValues[tile.key]"
                            :loading="loading")
      .chart-grid
        aiq-row(:gutter="16")
          aiq-col(:span="24")
            overview-chart(:dateRange="dateRangeUtc"
                           :chartConfig="dailyQueriesChartConfig"
                           visualization="aiq-bar-chart"
                           grouped
                           :formatXTick="formatChartXTick"
                           @onChartDataChange="onDailyQueriesDataChange"
                           @onXLabelsChange="dailyQueriesXLabels=$event"
                           :reportFileName="returnReportFileName('Daily Query Volume')"
                           :shouldHandlePartialDates="true")
</template>

<script>
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import { get, find } from 'lodash';
import {
  dstDateRangeUtc,
  datePickerDefaultOptions,
  defaultDateRange,
  lastAvailableMonthAndQuarterRange,
  createPickerDateRangeOption,
} from '../../configs/dateRange';
import dayjs from '@/localPlugins/dayjs';
import OverviewChart from '@/components/Chart.vue';
import { downloadAsFile } from '@/libs';
import SmartAssistTile, { formatTileValue } from './SmartAssistTile.vue';
import { SMART_ASSIST_TILES } from './constants';
import { PERCENT_MULTIPLIER } from '../../constants';
import dailyQueriesChartConfig from '../../configs/smartAssistDailyQueries';

const I18N_TILES_PREFIX = 'metrics_tab.smart_assist_tab.overview_tab.tiles';

// Returns num/den as a percentage, or null when the denominator is zero so
// the tile renders 'N/A' rather than a misleading 0%.
const safePercent = (num, den) => (den > 0 ? (num / den) * PERCENT_MULTIPLIER : null);

export default {
  pageResource: '/metrics',
  components: {
    ManagementPage,
    SmartAssistTile,
    OverviewChart,
  },
  data() {
    return {
      tiles: SMART_ASSIST_TILES,
      dateRange: defaultDateRange(),
      counts: {},
      loading: false,
      dailyQueriesChartConfig,
      dailyQueriesChartData: [],
      dailyQueriesXLabels: {},
    };
  },
  computed: {
    datePickerOptions() {
      return {
        ...datePickerDefaultOptions(),
        shortcuts: lastAvailableMonthAndQuarterRange.map(createPickerDateRangeOption),
      };
    },
    dateRangeUtc() {
      return dstDateRangeUtc(this.dateRange);
    },
    // Derives the seven displayed tile values from the raw counts returned by
    // POST /metrics/smart_assist. Rates are computed here (not server-side) so
    // divide-by-zero handling lives in one place.
    tileValues() {
      const c = this.counts;
      const answered = get(c, 'answered_count', 0);
      const unanswered = get(c, 'unanswered_count', 0);
      const liked = get(c, 'liked_count', 0);
      const disliked = get(c, 'disliked_count', 0);
      const queries = get(c, 'query_count', null);
      const sessions = get(c, 'session_count', null);
      return {
        queries,
        answer_rate: safePercent(answered, answered + unanswered),
        feedback: safePercent(liked, liked + disliked),
        users: get(c, 'user_count', null),
        feedback_rate: safePercent(get(c, 'voted_answered_count', 0), answered),
        sessions,
        queries_per_session: sessions > 0 ? queries / sessions : null,
      };
    },
  },
  watch: {
    dateRangeUtc: {
      immediate: true,
      handler() {
        this.fetchOverview();
      },
    },
  },
  methods: {
    tileTitle(tile) {
      return this.$t(`${I18N_TILES_PREFIX}.${tile.key}.title`);
    },
    formatChartXTick(date) {
      const label = this.dailyQueriesXLabels[dayjs(date).valueOf()] || dayjs(date).format('MMM DD');
      const partialIndicator = this.isDailyQueriesDataPartial(date) ? '*' : '';
      return `${label}${partialIndicator}`;
    },
    isDailyQueriesDataPartial(date) {
      const timeStamp = new Date(date).getTime();
      return find(this.dailyQueriesChartData, { x: timeStamp })?.isPartial;
    },
    onDailyQueriesDataChange(data) {
      this.dailyQueriesChartData = get(data, 'dataset[0].data_points', []);
    },
    returnReportFileName(chartName = 'data') {
      const [startTimestamp, endTimestamp] = this.dateRange;
      const startDate = dayjs.isDayjs(startTimestamp)
        ? startTimestamp.format('YYYY-MM-DD')
        : dayjs(startTimestamp).format('YYYY-MM-DD');
      const endDate = dayjs.isDayjs(endTimestamp)
        ? endTimestamp.format('YYYY-MM-DD')
        : dayjs(endTimestamp).format('YYYY-MM-DD');
      return `${chartName.replaceAll(' ', '_')}_${startDate}_${endDate}`;
    },
    tileHelp(tile) {
      return this.$t(`${I18N_TILES_PREFIX}.${tile.key}.help_text`);
    },
    exportTiles() {
      const rows = this.tiles.map(tile => ({
        Metric: this.tileTitle(tile),
        Value: formatTileValue(tile.category, this.tileValues[tile.key], { localized: false }),
      }));
      downloadAsFile({
        filename: this.returnReportFileName('Smart Assist Overview'),
        data: rows,
        dataType: 'table',
      });
    },
    async fetchOverview() {
      const [period_start, period_end] = this.dateRangeUtc;
      if (!(period_start && period_end)) {
        return;
      }
      this.loading = true;
      try {
        const data = await this.$store.dispatch('metrics/getMetric', [
          'smart_assist',
          { period_start, period_end },
        ]);
        this.counts = get(data, 'dataset', {});
      } catch (err) {
        this.$aiq.notify.error(
          this.$t('metrics_tab.smart_assist_tab.overview_tab.errors.load_failed'),
        );
        this.counts = {};
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/aiq-variables.scss";

.chart-configuration {
  display: flex;
  margin-bottom: 24px;
}

.time-range-filter {
  position: relative;
  margin-left: auto;
}

.tiles-card {
  background: $aiq-bgc-list-item;
  border: 1px solid #e4e7ed;
  padding: 24px;
}

.tiles-card-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.export-tiles {
  height: 30px;
  width: 40px;
  padding: 0;
}

.tiles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.chart-grid {
  margin-top: 16px;
}
</style>

<style lang="scss">
@import "../../../../styles/aiq-variables-dark.scss";

[data-theme="dark"] .tiles-card {
  background: $aiq-bgc-sidebar-item-hover;
  border-color: $aiq-bdc-input-default;
}
</style>
